import Phaser from 'phaser';
import type { MassSystem } from '@/systems/MassSystem';
import type { Entity } from '@/types';

const BLOB_TEXTURE_KEY = 'blob-placeholder';

export class BlobEntity extends Phaser.Physics.Arcade.Image {
  readonly entityId = 'blob';
  readonly speciesId = 'blob';

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    private readonly massSystem: MassSystem,
  ) {
    BlobEntity.ensureTexture(scene);
    super(scene, x, y, BLOB_TEXTURE_KEY);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setCircle(14);
    this.setCollideWorldBounds(true);
    this.setDamping(true);
    this.setDrag(0.9);
    this.setMaxVelocity(200, 200);
    this.applyTierScale(massSystem.currentTier, false);

    massSystem.onTierChange((tier) => {
      this.applyTierScale(tier, true);
    });
  }

  get entity(): Entity {
    return {
      id: this.entityId,
      speciesId: this.speciesId,
      mass: this.massSystem.currentMass,
    };
  }

  /**
   * Smoothly steer toward a target velocity. Gives ooze-like acceleration
   * and deceleration instead of snapping. Zero input decays via drag.
   *
   * `input` is a vector in [-1, 1] per axis (keyboard / joystick tilt).
   * `maxSpeed` is the cap the blob can reach at current tier.
   */
  applyImpulse(ax: number, ay: number, maxSpeed: number): void {
    const body = this.body as Phaser.Physics.Arcade.Body | null;
    if (!body) return;
    const targetVx = ax * maxSpeed;
    const targetVy = ay * maxSpeed;
    const lerp = 0.08; // smaller = smoother / slower to respond
    const nextVx = body.velocity.x + (targetVx - body.velocity.x) * lerp;
    const nextVy = body.velocity.y + (targetVy - body.velocity.y) * lerp;
    this.setVelocity(nextVx, nextVy);
  }

  get aimDirection(): { x: number; y: number } {
    const v = this.body?.velocity;
    if (!v) return { x: 1, y: 0 };
    const len = Math.hypot(v.x, v.y);
    if (len < 0.1) return { x: 1, y: 0 };
    return { x: v.x / len, y: v.y / len };
  }

  teleport(x: number, y: number): void {
    this.setPosition(x, y);
    this.setVelocity(0, 0);
  }

  private applyTierScale(tier: number, tween: boolean): void {
    const target = 0.6 + tier * 0.2;
    if (tween) {
      this.scene.tweens.add({
        targets: this,
        scale: target,
        duration: 400,
        ease: 'Back.easeOut',
      });
    } else {
      this.setScale(target);
    }
  }

  private static ensureTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists(BLOB_TEXTURE_KEY)) return;
    const g = scene.add.graphics();
    g.fillStyle(0x6cff6c, 1);
    g.fillCircle(16, 16, 14);
    g.lineStyle(2, 0x2d8f2d, 1);
    g.strokeCircle(16, 16, 14);
    g.fillStyle(0x9cff9c, 0.6);
    g.fillCircle(12, 12, 4);
    g.generateTexture(BLOB_TEXTURE_KEY, 32, 32);
    g.destroy();
  }
}
